import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { pageAsOptions } from "../utils/commonDataUtils";
import { createAxiosInstance } from "./ServicesHelpers";
import { Currency } from "../pages/companies/ProfilePage";
import { fetchProvider } from "./ProviderService";
import { fetchProfile } from "./ProfilesService";
import { fetchAccount } from "./AccountService";
import { fetchRoutingPlan } from "./RoutingPlanService";

export type RatioUpdateMethod = "MANUAL" | "API";

export interface ICurrency {
  id: number;
  name: string;
  ratioToReference: number;
  ratioUpdateMethod: RatioUpdateMethod;
  currencyProviderId?: number | null;
  lastRatioUpdate?: string;
  isDeleted?: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdDate?: string;
  timestamp?: string;
  version?: number;
}

export interface ICurrencyHistory {
  id: string;
  snapshot: ICurrency & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  status: string;
  createdBy: string;
}

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export const fetchCurrencies = async (
  params: Params<ICurrency>
): Promise<IPageable<ICurrency>> => {
  try {
    const response = await axiosInstance.get("/currencies", { params: { isDeleted: false, ...params } });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the currencies."
    );
  }
};

export const fetchAllCurrencies = async (): Promise<ICurrency[]> => {
  try {
    const response = await axiosInstance.get("/currencies/all", {
      params: { isDeleted: false },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching all currencies."
    );
  }
};

export const fetchCurrency = async (
  id: ICurrency["id"]
): Promise<ICurrency> => {
  try {
    const response = await axiosInstance.get(`/currencies/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching the currencies/${id}.`
    );
  }
};

export const addCurrencyApi = async (
  data: Partial<ICurrency>
): Promise<ICurrency> => {
  try {
    const response = await axiosInstance.post("/currencies", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the currency."
    );
  }
};

export const editCurrencyApi = async (
  id: number | string,
  data: Partial<ICurrency>
): Promise<ICurrency> => {
  try {
    const response = await axiosInstance.put(`/currencies/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the currency."
    );
  }
};

export const deleteCurrencyApi = async (
  id: number | string
): Promise<void> => {
  try {
    await axiosInstance.delete(`/currencies/${id}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the currency."
    );
  }
};

export const exportCurrenciesCsv = async (): Promise<Blob> => {
  try {
    const response = await axiosInstance.get("/currencies/csv", {
      responseType: "blob",
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while exporting currencies."
    );
  }
};

export const fetchCurrencyHistory = async (
  id: ICurrency["id"],
  params: Params<ICurrency> = {}
): Promise<IPageable<ICurrency>> => {
  try {
    const response = await axiosInstance.get(`/currencies/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the currency history for ID ${id}.`
    );
  }
};

export const useCurrenciesQuery = (
  params: Params<ICurrency> = {}
): UseQueryResult<Currency[], Error> => {
  const queryKey = ["currency", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: async () => {
      const mergedParams = {
        sort: "name",
        size: config.MAX_PAGE_SIZE,
        ...params,
      };
      const currencyPage = await fetchCurrencies(mergedParams);
      return pageAsOptions(currencyPage).map((option) => ({
        id: Number(option.value),
        name: option.label,
      }));
    },
  });
};

export const useCurrencyByIdQuery = (
  currencyId?: number
): UseQueryResult<Currency, Error> => {
  const queryKey = ["currency", currencyId];

  return useQuery({
    queryKey,
    queryFn: async () => {
      return await fetchCurrency(Number(currencyId));
    },
    enabled: Boolean(currencyId),
  });
};

export const useCurrenciesPageQuery = (
  params: Params<ICurrency> & { _excludeId?: number }
): UseQueryResult<IPageable<ICurrency>, Error> => {
  const { _excludeId, ...rest } = params as Params<ICurrency> & { _excludeId?: number };
  const effectiveParams = {
    ...rest,
    sort: rest.sort || "name,asc",
    isDeleted: false,
  };
  const queryKey = ["currenciesPage", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchCurrencies(effectiveParams),
    ...(_excludeId !== undefined && {
      select: (data: IPageable<ICurrency>): IPageable<ICurrency> => ({
        ...data,
        content: data.content.filter((c) => c.id !== _excludeId),
      }),
    }),
  });
};

export const getProviderCurrencyOptions = (providerId: string) => ({
  queryKey: ["providerCurrency", providerId],
  queryFn: async (): Promise<string> => {
    const provider = await fetchProvider(providerId);
    const profile = await fetchProfile(provider.profileId);
    const currency = await fetchCurrency(profile.currencyId);

    return currency?.name;
  },
  select: (currencyName: string) => currencyName,
});

export const getCurrencyOptions = (profileId: string) => ({
  queryKey: ["currencyId", profileId],
  queryFn: async (): Promise<string> => {
    const profile = await fetchProfile(profileId);
    const currency = await fetchCurrency(profile.currencyId);
    return currency?.name;
  },
  select: (currencyName: string) => currencyName,
});

export const getCurrencyNameOptions = (currencyId: number) => ({
  queryKey: ["currencyName", currencyId],
  queryFn: async (): Promise<string> => {
    const currency = await fetchCurrency(currencyId);
    return currency?.name;
  },
  select: (currencyName: string) => currencyName,
});

export const getConnectionProfileCurrencyOptions = (connectionProfileId: number | string) => ({
  queryKey: ["connectionProfileCurrency", connectionProfileId],
  queryFn: async (): Promise<string> => {
    try {
      const account = await fetchAccount(String(connectionProfileId));
      if (!account.profileId) return "-";
      const profile = await fetchProfile(account.profileId);
      const currency = await fetchCurrency(Number(profile.currencyId));
      return currency?.name || "-";
    } catch {
      return "-";
    }
  },
  select: (currencyName: string) => currencyName,
});

export const getRoutingPlanCurrencyOptions = (routingPlanId: string) => ({
  queryKey: ["routingPlanCurrency", routingPlanId],
  queryFn: async (): Promise<string> => {
    try {
      const routingPlan = await fetchRoutingPlan(routingPlanId);
      if (!routingPlan.currencyId) return "-";
      const currency = await fetchCurrency(routingPlan.currencyId);
      return currency?.name || "-";
    } catch {
      return "-";
    }
  },
  select: (currencyName: string) => currencyName,
});
